import { type SetMessages } from '.';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import { effectiveTier } from '~/utils/effectiveTier';
import { FAILED_DEPLOYMENT_MARKER } from '~/utils/markers';

interface DeployStatus {
  site_url: string;
  deploy_url: string;
  claim_url: string;
}

/**
 * This logic is duplicated in streamToolGetDeploymentStatus.
 */
export async function getDeploymentStatus({ setMessages }: { setMessages: SetMessages }) {
  const deployStatus = workbenchStore.lastArtifact?.runner.getDeployStatus();

  try {
    if (!deployStatus) {
      throw new Error('No deployment status');
    }

    const status = await deployStatus;

    const user = userStore.getUser();
    const userTier = user && effectiveTier(user);
    const supportCustomDomains = user?.featureFlags.hackathon && userTier?.plan !== 'personal';

    const { site_url: siteUrl, deploy_url: deployUrl, claim_url: claimUrl } = status as unknown as DeployStatus;

    const url = siteUrl ?? deployUrl;

    if (!url) {
      throw new Error('Missing deployment URL');
    }

    // only show the custom domain setup message if the deploy URL is a Netlify URL and it's supported
    const canSetupDomain = supportCustomDomains && (url.endsWith('.bolt.host') || url.endsWith('.bolthost.dev'));

    setMessages((messages) => {
      const lastMessage = messages.at(-1)!;

      lastMessage.parts.push({
        type: 'text',
        text: `\n\nYour site has been successfully published! You can view it at: ${url}`,
      });

      if (claimUrl) {
        lastMessage.parts.push({
          type: 'text',
          text: `\n\nTo transfer this Netlify project to your own account, you can use this claim URL:\n${claimUrl}`,
        });
      }

      if (canSetupDomain) {
        lastMessage.parts.push({
          type: 'text',
          text: `\n\nYou can setup a custom domain by either [purchasing a new domain](#__bolt-settings__/domains/buyNewDomain) or [use a domain you already own](#__bolt-settings__/domains/addCustomDomain).`,
        });
      }

      lastMessage.status = 'complete';

      lastMessage.content = lastMessage.parts.flatMap((part) => (part.type === 'text' ? part.text : [])).join('');

      return messages;
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);

    setMessages((messages) => {
      const lastMessage = messages.at(-1)!;

      lastMessage.parts.push({
        type: 'text',
        text: `\n\nFailed to publish the project.\n\n${message}`,
      });

      lastMessage.parts.push({
        type: 'text',
        text: FAILED_DEPLOYMENT_MARKER,
      });

      lastMessage.content = lastMessage.parts.flatMap((part) => (part.type === 'text' ? part.text : [])).join('');

      return messages;
    });
  } finally {
    workbenchStore.isStreaming.set(false);
  }
}
